import { useEffect, useRef, useState } from 'react'
import Cropper from 'react-easy-crop'
import {
  Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Slider, Stack, Typography, alpha, useTheme,
} from '@mui/material'
import { DeleteOutline, EditOutlined, PhotoCamera, RotateRight, ZoomIn, ZoomOut } from '@mui/icons-material'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import { avatarColor, avatarSrc, getInitials } from '../../utils/avatar'

const MAX_BYTES = 5 * 1024 * 1024
const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
const OUT = 512                                   // saved picture: 512 x 512

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })

// Cuts the chosen square (after rotation) out of the image
const cropToBlob = async (src, area, rotation) => {
  const img = await loadImage(src)
  const rad = (rotation * Math.PI) / 180
  const sin = Math.abs(Math.sin(rad))
  const cos = Math.abs(Math.cos(rad))
  const bw = img.width * cos + img.height * sin
  const bh = img.width * sin + img.height * cos

  const rotated = document.createElement('canvas')
  rotated.width = bw
  rotated.height = bh
  const rctx = rotated.getContext('2d')
  rctx.translate(bw / 2, bh / 2)
  rctx.rotate(rad)
  rctx.drawImage(img, -img.width / 2, -img.height / 2)

  const out = document.createElement('canvas')
  out.width = OUT
  out.height = OUT
  out.getContext('2d').drawImage(rotated, area.x, area.y, area.width, area.height, 0, 0, OUT, OUT)

  return new Promise((resolve) => out.toBlob(resolve, 'image/jpeg', 0.9))
}

// menu: a small "Edit" button on the photo opens Upload / Remove (My Profile); otherwise plain buttons (Edit user)
// onFile: nothing is uploaded; the cropped picture (or null) is handed over, e.g. for a user that isn't created yet
const AvatarEditor = ({ user, onChanged, onFile, vertical = false, menu = false }) => {
  const theme = useTheme()
  const axiosPrivate = useAxiosPrivate()
  const inputRef = useRef(null)

  const isDark = theme.palette.mode === 'dark'
  const accent = theme.palette.secondary.main
  const accentText = theme.palette.getContrastText(accent)

  const [current, setCurrent] = useState(user?.avatar || null)
  const [src, setSrc] = useState(null)             // picked image (object URL); the crop dialog is open while set
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [area, setArea] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [menuAnchor, setMenuAnchor] = useState(null)

  useEffect(() => { setCurrent(user?.avatar || null) }, [user?.avatar])
  useEffect(() => () => { if (src) URL.revokeObjectURL(src) }, [src])
  // Local preview of a picture that isn't uploaded yet (onFile mode)
  useEffect(() => () => { if (current?.startsWith('blob:')) URL.revokeObjectURL(current) }, [current])

  const closeCrop = () => {
    setSrc(null); setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); setArea(null)
  }

  const handlePick = (e) => {
    const picked = e.target.files?.[0]
    e.target.value = ''
    if (!picked) return
    if (!ALLOWED.includes(picked.type)) return setError('Please choose a JPG, PNG, WEBP or GIF image.')
    if (picked.size > MAX_BYTES) return setError('The image must be 5 MB or smaller.')
    setError('')
    setSrc(URL.createObjectURL(picked))
  }

  const handleApply = async () => {
    if (!src || !area) return
    setBusy(true)
    setError('')
    try {
      const blob = await cropToBlob(src, area, rotation)
      if (onFile) {
        setCurrent(URL.createObjectURL(blob))
        onFile(blob)
        closeCrop()
        return
      }
      const form = new FormData()
      form.append('avatar', blob, 'avatar.jpg')
      const { data } = await axiosPrivate.put(`/api/users/${user._id}/avatar`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setCurrent(data.avatar)
      onChanged?.(data.avatar)
      closeCrop()
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to upload the picture')
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async () => {
    if (onFile) {
      setCurrent(null)
      onFile(null)
      return
    }
    setBusy(true)
    setError('')
    try {
      await axiosPrivate.delete(`/api/users/${user._id}/avatar`)
      setCurrent(null)
      onChanged?.(null)
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to remove the picture')
    } finally {
      setBusy(false)
    }
  }

  const avatarSx = (size, fontSize) => ({
    width: size,
    height: size,
    fontSize,
    ...avatarColor(user?.name || '', theme.palette.mode),
    boxShadow: `0 0 0 3px ${alpha(accent, 0.65)}`,
  })

  return (
    <>
      {menu ? (
        <Box sx={{ position: 'relative', width: 160, height: 160 }}>
          <Avatar src={avatarSrc(current)} sx={avatarSx(160, '3.6rem')}>
            {getInitials(user?.name || '')}
          </Avatar>

          <Button
            size="small"
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <EditOutlined fontSize="small" />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            disabled={busy}
            aria-haspopup="menu"
            sx={{
              position: 'absolute',
              left: 4,
              bottom: 8,
              px: 1.25,
              py: 0.25,
              minWidth: 0,
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              // See-through so the photo shows behind the label
              color: '#fff',
              backgroundColor: 'rgba(0,0,0,0.28)',
              backdropFilter: 'blur(2px)',
              border: '1px solid rgba(255,255,255,0.45)',
              textShadow: '0 1px 2px rgba(0,0,0,0.6)',   // keeps the label readable on light photos
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.45)', borderColor: accent },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.7)' },
            }}
          >
            Edit
          </Button>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                mt: 0.75,
                minWidth: 200,
                backgroundColor: theme.palette.background.alt,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200]}`,
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null)
                inputRef.current?.click()
              }}
            >
              <ListItemIcon sx={{ color: accent }}>
                <PhotoCamera fontSize="small" />
              </ListItemIcon>
              <ListItemText>{current ? 'Change photo…' : 'Upload a photo…'}</ListItemText>
            </MenuItem>

            {current && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null)
                  handleRemove()
                }}
                sx={{ color: theme.palette.error.main }}
              >
                <ListItemIcon sx={{ color: theme.palette.error.main }}>
                  <DeleteOutline fontSize="small" />
                </ListItemIcon>
                <ListItemText>Remove photo</ListItemText>
              </MenuItem>
            )}
          </Menu>

          <input ref={inputRef} type="file" hidden accept={ALLOWED.join(',')} onChange={handlePick} />
        </Box>
      ) : (
      <Stack direction={vertical ? 'column' : 'row'} alignItems="center" spacing={vertical ? 2.5 : 2}>
        <Avatar
          src={avatarSrc(current)}
          sx={{
            width: vertical ? 140 : 88,
            height: vertical ? 140 : 88,
            fontSize: vertical ? '3.2rem' : '2rem',
            ...avatarColor(user?.name || '', theme.palette.mode),
            boxShadow: `0 0 0 3px ${alpha(accent, 0.65)}`,
          }}
        >
          {getInitials(user?.name || '')}
        </Avatar>

        <Stack spacing={1} alignItems={vertical ? 'center' : 'flex-start'}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PhotoCamera />}
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            sx={{ color: accent, borderColor: alpha(accent, 0.6), fontWeight: 700 }}
          >
            {current ? 'Change photo' : 'Upload photo'}
          </Button>

          {current && (
            <Button
              size="small"
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <DeleteOutline />}
              onClick={handleRemove}
              disabled={busy}
              sx={{ color: theme.palette.error.main, fontWeight: 700 }}
            >
              Remove
            </Button>
          )}
        </Stack>

        <input ref={inputRef} type="file" hidden accept={ALLOWED.join(',')} onChange={handlePick} />
      </Stack>
      )}

      {error && !src && (
        <Typography variant="caption" color="error" display="block" mt={1} textAlign={vertical || menu ? 'center' : 'left'}>{error}</Typography>
      )}

      {/* Adjust: drag to move, slider to zoom, button to rotate */}
      <Dialog
        open={Boolean(src)}
        onClose={busy ? undefined : closeCrop}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.alt,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200]}`,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}` }}>
          Adjust picture
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ position: 'relative', height: 320, backgroundColor: '#000' }}>
            {src && (
              <Cropper
                image={src}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={(_, pixels) => setArea(pixels)}
              />
            )}
          </Box>

          <Stack direction="row" alignItems="center" spacing={1.5} px={2} py={1.5}>
            <ZoomOut fontSize="small" />
            <Slider
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(_, v) => setZoom(v)}
              sx={{ color: accent }}
              aria-label="Zoom"
            />
            <ZoomIn fontSize="small" />
            <IconButton size="small" onClick={() => setRotation((r) => (r + 90) % 360)} aria-label="Rotate">
              <RotateRight />
            </IconButton>
          </Stack>

          {error && (
            <Typography variant="caption" color="error" display="block" px={2} pb={1}>{error}</Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={closeCrop} disabled={busy} sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={busy || !area}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PhotoCamera />}
            sx={{
              backgroundColor: accent, color: accentText, fontWeight: 700, boxShadow: 'none',
              '&:hover': { backgroundColor: theme.palette.secondary.light, boxShadow: 'none' },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AvatarEditor